import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ChartData } from '../../store/chartAtoms'; // Or FullChart if that's what chartToRenderForPreview is
import type { AxisOptions } from '../../store/chartAtoms'; // Assuming AxisOptions will be defined here

interface ChartAxisSettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  chartDataForPreview: ChartData | any; // Use a more specific type once chartToRenderForPreview is refined
  initialAxisOptions?: AxisOptions | null;
  onSaveAxisOptions: (newOptions: AxisOptions) => void;
}

const ChartAxisSettingsDialog: React.FC<ChartAxisSettingsDialogProps> = ({
  isOpen,
  onClose,
  chartDataForPreview,
  initialAxisOptions,
  onSaveAxisOptions,
}) => {
  const [xMin, setXMin] = useState<string | number | undefined>(undefined);
  const [xMax, setXMax] = useState<string | number | undefined>(undefined);
  const [yMin, setYMin] = useState<number | undefined>(undefined);
  const [yMax, setYMax] = useState<number | undefined>(undefined);

  const [xAxisType, setXAxisType] = useState<'numeric' | 'date' | 'category'>('category');

  useEffect(() => {
    // Determine the nature of the X-axis based on current chart data (passed as chartDataForPreview)
    let calculatedXAxisType: 'numeric' | 'date' | 'category' = 'category';
    const chart = chartDataForPreview;
    let sampleXValue: any = null;

    if (chart?.data?.[0]?.[chart.xAxis]) { // Local ChartData structure
        sampleXValue = chart.data[0][chart.xAxis];
    } else if (chart?.datasets?.[0]?.data?.[0]?.label) { // DBChart structure
        sampleXValue = chart.datasets[0].data[0].label;
    }

    if (typeof sampleXValue === 'number') {
        calculatedXAxisType = 'numeric';
    } else if (typeof sampleXValue === 'string') {
        // More robust date checking - does it look like a YYYY-MM-DD or MM/DD/YYYY?
        if (/^\d{4}-\d{2}-\d{2}/.test(sampleXValue) || /^\d{1,2}\/\d{1,2}\/\d{2,4}/.test(sampleXValue)) {
            // Further check if it's a valid date object if parsed
            if (!isNaN(new Date(sampleXValue).getTime())) {
                 calculatedXAxisType = 'date';
            }
        }
        // Otherwise, it remains 'category' (e.g., "Jan", "Feb", "Q1")
    }
    setXAxisType(calculatedXAxisType);

    // Initialize from initialAxisOptions, defaulting to undefined (empty inputs)
    // This prevents auto-filling X-axis inputs if they were originally null/undefined (auto)
    setXMin(initialAxisOptions?.xMin === null ? undefined : initialAxisOptions?.xMin);
    setXMax(initialAxisOptions?.xMax === null ? undefined : initialAxisOptions?.xMax);
    setYMin(initialAxisOptions?.yMin === null ? undefined : initialAxisOptions?.yMin);
    setYMax(initialAxisOptions?.yMax === null ? undefined : initialAxisOptions?.yMax);

}, [initialAxisOptions, chartDataForPreview]);

const handleSave = () => {
    let finalXMin: string | number | null = null;
    let finalXMax: string | number | null = null;

    // Only process xMin/xMax if the type is not category and inputs have values
    if (xAxisType === 'date') {
        finalXMin = (xMin === '' || xMin === undefined) ? null : (xMin as string).trim();
        finalXMax = (xMax === '' || xMax === undefined) ? null : (xMax as string).trim();
        if (finalXMin === '') finalXMin = null; // Ensure empty string becomes null
        if (finalXMax === '') finalXMax = null;
        // Basic validation: if one is set, the other should ideally be too for dates, but allow partial for now.
        // More complex validation (e.g. min < max) can be added.
    } else if (xAxisType === 'numeric') {
        finalXMin = (xMin === '' || xMin === undefined || isNaN(Number(xMin))) ? null : Number(xMin);
        finalXMax = (xMax === '' || xMax === undefined || isNaN(Number(xMax))) ? null : Number(xMax);
    }
    // If xAxisType is 'category', finalXMin and finalXMax remain null.

    const finalYMin = (yMin === undefined || yMin === null || isNaN(Number(yMin))) ? null : Number(yMin);
    const finalYMax = (yMax === undefined || yMax === null || isNaN(Number(yMax))) ? null : Number(yMax);

    const newOptions: AxisOptions = {
        xMin: finalXMin,
        xMax: finalXMax,
        yMin: finalYMin,
        yMax: finalYMax,
    };
    console.log('[ChartAxisSettingsDialog] Saving options:', newOptions);
    onSaveAxisOptions(newOptions);
    onClose();
};

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Chart Axis Settings</DialogTitle>
          <DialogDescription>
            Define custom ranges for the X and Y axes. Leave blank for automatic scaling.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <h4 className="font-medium text-sm text-muted-foreground col-span-2">X-Axis Range</h4>
          {xAxisType === 'date' ? (
            <>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="xMinDate" className="text-right">
                  Min Date
                </Label>
                <Input 
                  id="xMinDate" 
                  type="date" 
                  value={xMin as string || ''} 
                  onChange={(e) => setXMin(e.target.value)} 
                  className="col-span-3" 
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="xMaxDate" className="text-right">
                  Max Date
                </Label>
                <Input 
                  id="xMaxDate" 
                  type="date" 
                  value={xMax as string || ''} 
                  onChange={(e) => setXMax(e.target.value)} 
                  className="col-span-3" 
                />
              </div>
            </>
          ) : xAxisType === 'numeric' ? (
            <>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="xMinNum" className="text-right">
                  Min Value
                </Label>
                <Input 
                    id="xMinNum" 
                    type="number" 
                    value={xMin as number ?? ''} 
                    onChange={(e) => setXMin(e.target.value === '' ? undefined : parseFloat(e.target.value))} 
                    className="col-span-3" 
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="xMaxNum" className="text-right">
                  Max Value
                </Label>
                <Input 
                    id="xMaxNum" 
                    type="number" 
                    value={xMax as number ?? ''} 
                    onChange={(e) => setXMax(e.target.value === '' ? undefined : parseFloat(e.target.value))} 
                    className="col-span-3" 
                />
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground col-span-2">X-axis is categorical, range cannot be set.</p>
          )}

          <h4 className="font-medium text-sm text-muted-foreground col-span-2 pt-4">Y-Axis Range</h4>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="yMin" className="text-right">
              Min Value
            </Label>
            <Input 
                id="yMin" 
                type="number" 
                value={yMin ?? ''} 
                onChange={(e) => setYMin(e.target.value === '' ? undefined : parseFloat(e.target.value))} 
                className="col-span-3" 
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="yMax" className="text-right">
              Max Value
            </Label>
            <Input 
                id="yMax" 
                type="number" 
                value={yMax ?? ''} 
                onChange={(e) => setYMax(e.target.value === '' ? undefined : parseFloat(e.target.value))} 
                className="col-span-3" 
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>Save Settings</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChartAxisSettingsDialog; 